import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNavigate, useParams } from 'react-router-dom'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import {
  Activity,
  Award,
  Building2,
  Check,
  FileText,
  HardDrive,
  Loader2,
  Pencil,
  Sparkles,
  Star,
  Users,
  X,
} from 'lucide-react'
// Same `react-phone-number-input` source the Companies table CountryCell
// + Users modal CountryValue use, so the flag + label in this dashboard
// matches the row the admin opened it from.
import countryLabels from 'react-phone-number-input/locale/en.json'
import Flags from 'react-phone-number-input/flags'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { Breadcrumb } from '../../components/Breadcrumb'
import { StatusPill } from '../../components/ui/Pills'
import { api, extractApiError } from '../../lib/api'
import { emailSchema } from '../../lib/validators'
import { formatDateTime, formatNumber } from '../../lib/format'

/**
 * Master-Admin per-company dashboard — full page (with back button
 * to the Companies list). Opened by clicking a company Name in the
 * Companies table. Read-only on purpose: every mutation lives on the
 * row's action icons back on the list so opening / navigating into
 * the dashboard is side-effect-free.
 *
 * Fetches the rich `/super-admin/tenants/:id` payload (owner + 11
 * metrics in one Promise.all round-trip) and lays it out as
 * identity → KPI strip → owner → company details. The data
 * vocabulary is the same as the Companies-table row the admin
 * clicked through from, so values agree on both surfaces.
 */
export function TenantDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  // Re-uses the `platform-tenant` query key that `TenantRowActions`
  // already invalidates after every mutation — so suspending /
  // editing a tenant from the row action cluster and then re-
  // opening this dashboard shows fresh data without an extra fetch.
  const detailQuery = useQuery({
    queryKey: ['platform-tenant', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get(`/super-admin/tenants/${id}`)
      return res.data
    },
  })
  // Don't strand the viewer on a dead dashboard when the company is deleted (or
  // the URL is stale) — return to the companies list with a notice.
  useRedirectOnNotFound(detailQuery, () => navigate('/super-admin/companies'), 'This company is no longer available.')

  return (
    <div>
      {/* Three-level trail: Dashboard > Companies > {Name}. The
       *  intermediate "Companies" crumb is a real link, and it is the
       *  ONLY route back to the list — a separate "Back to Companies"
       *  button used to sit directly beneath this, offering the same
       *  destination twice within 40px of each other (QA, 2026-09-01).
       *  The breadcrumb wins: it is the pattern on all 20 platform
       *  pages, and it also says where you are, not just where you can
       *  go back to.
       *
       *  NB this is not the same as the "Back to templates" button on
       *  the Email Template editor — that one lives inside a "Template
       *  Not Found" error state, where it is the primary recovery
       *  action rather than duplicate chrome. */}
      <Breadcrumb
        current={detailQuery.data?.name ?? 'Company'}
        homePath="/super-admin/dashboard"
        trail={[{ label: 'Companies', to: '/super-admin/companies' }]}
      />

      {detailQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading company…
        </p>
      ) : detailQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(detailQuery.error).message}
        </p>
      ) : detailQuery.data ? (
        <Body data={detailQuery.data} tenantId={id} />
      ) : null}
    </div>
  )
}

function Body({ data, tenantId }) {
  const m = data.metrics ?? {}
  return (
    <div className="space-y-6">
      {/* Identity header — company icon tile + name + slug + pills.
       *  Larger than the modal version because we have a whole page
       *  to play with: the icon tile bumps up to 16×16 and the name
       *  flows at h1 size. */}
      <div className="flex items-start gap-4">
        <span className="h-16 w-16 rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] flex items-center justify-center shrink-0">
          <Building2 size={26} />
        </span>
        <div className="min-w-0 flex-1">
          {/* Slug used to render here as a muted sub-line, but the
           *  Company Details table below already surfaces it under its
           *  own row — and the master admin almost never needs the
           *  slug to scan a tenant. Keeping it only in the details
           *  table removes the visual duplication. */}
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] leading-tight truncate">
            {data.name}
          </h1>
          {/* Short page subtitle — matches the "Manage every company.
           *  N companies." pattern used on the Companies index. Gives
           *  the master admin a one-sentence read of what this page
           *  is for before the pills + KPIs below load in. */}
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Company details, plan, usage, and activity.
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <PlanPill plan={data.planName ?? data.planTier} />
            <TenantStatusPill status={data.displayStatus ?? data.status} />
            <CountryChip
              country={data.country}
              billingCurrency={data.billingCurrency}
            />
          </div>
        </div>
      </div>

      {/* KPI strip — 8 tiles in two rows, scoped to this company. Same
       *  visual vocabulary as the Companies + Plans KPI strips so the
       *  master-admin dashboard reads with one rhythm. */}
      <Section title="At a glance">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} tone="violet" label="Members" value={m.memberCount} />
          <Kpi icon={Activity} tone="emerald" label="Tasks" value={m.taskCount} />
          <Kpi icon={FileText} tone="amber" label="Reports" value={m.reportCount} />
          <Kpi
            icon={HardDrive}
            tone="sky"
            label="Storage"
            value={formatBytes(m.storageBytes)}
            hint={`${formatNumber(m.fileCount ?? 0)} file${m.fileCount === 1 ? '' : 's'}`}
          />
          <Kpi
            icon={Star}
            tone="amber"
            label="Total points"
            value={formatNumber(Number(m.totalPoints ?? 0))}
            hint={`${formatNumber(m.badgeCount ?? 0)} badge${m.badgeCount === 1 ? '' : 's'}`}
          />
          <Kpi icon={Award} tone="rose" label="SOPs" value={m.sopCount} />
          <Kpi
            icon={Sparkles}
            tone="violet"
            label="AI tokens (this month)"
            value={formatNumber(Number(m.aiTokensThisMonth ?? 0))}
            hint={`${formatNumber(m.aiCallsThisMonth ?? 0)} call${m.aiCallsThisMonth === 1 ? '' : 's'}`}
          />
        </div>
      </Section>

      {/* Two-column layout for owner + company details — page has the
       *  horizontal room to put them side-by-side on desktop, and
       *  stacking on mobile keeps the page readable on narrow screens. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Owner">
          {data.owner ? (
            <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)]">
              <Row label="Name" value={data.owner.fullName} />
              <OwnerEmailRow tenantId={tenantId} owner={data.owner} />
              <Row
                label="Contact number"
                value={formatContact(data.owner.contactNumber)}
              />
              <Row
                label="Status"
                value={
                  <StatusPill
                    status={data.owner.status}
                    companyStatus={data.status}
                  />
                }
              />
              <Row
                label="Last active"
                value={formatDateTime(data.owner.lastActiveAt)}
              />
            </div>
          ) : (
            <p className="text-sm text-[color:var(--color-ink-muted)] rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-6 text-center">
              No Company Admin currently on this company.
            </p>
          )}
        </Section>

        <Section title="Company details">
          <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)]">
            <Row label="Name" value={data.name} />
            <Row
              label="Plan"
              value={<PlanPill plan={data.planName ?? data.planTier} />}
            />
            {/* "Company Status" removed — it rendered the exact same value as
             *  the status chip in the header (`displayStatus ?? status`), so
             *  the page showed the company's status twice (QA, 2026-09-01).
             *  The header keeps it: status is the most operationally
             *  important fact on this page and belongs where the eye lands
             *  first, beside the company name, without scrolling.
             *
             *  NB the "Status" row in the OWNER section above is NOT a
             *  duplicate of this — it renders `data.owner.status`, the
             *  owner's own account state, which can differ from the
             *  company's (an owner may be suspended while the company is
             *  active). It stays. */}
            <Row
              label="Country"
              value={
                <CountryChip
                  country={data.country}
                  billingCurrency={data.billingCurrency}
                />
              }
            />
            <Row
              label="Origin"
              value={ORIGIN_LABEL[data.createdVia] ?? 'Self signup'}
            />
            {data.trialEndsAt ? (
              <Row label="Trial ends" value={formatDateTime(data.trialEndsAt)} />
            ) : null}
            <Row label="Created" value={formatDateTime(data.createdAt)} />
          </div>
        </Section>
      </div>
    </div>
  )
}

// ─── presentation primitives ──────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div>
      <h3 className="mt-0 mb-2 text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        {title}
      </h3>
      {children}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-3 px-4 py-2.5">
      {/* Label + value share the same `text-sm` size so the two
       *  columns read at the same scale. The label stays in
       *  `ink-muted` to keep its supporting role, but bumping it from
       *  `text-xs` removes the size mismatch that was making the rows
       *  feel inconsistent. */}
      <span className="text-sm text-[color:var(--color-ink-muted)] self-center">
        {label}
      </span>
      <span className="text-sm text-[color:var(--color-ink-strong)] min-w-0 break-words">
        {value || '—'}
      </span>
    </div>
  )
}

/** The company owner's "Email ID" row, made editable for the Master Admin — the
 *  one mutating control on this otherwise read-only dashboard. Matches the plain
 *  `Row` layout, expanding to an inline input on Edit. The endpoint re-gates on
 *  super_admin and refuses a shared / super_admin / foreign target, so the UI's
 *  job is only to collect + validate the new address and surface the result
 *  (including the "shared Identity — change it from Profile" refusal). */
function OwnerEmailRow({ tenantId, owner }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(owner.emailId ?? '')
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: async (newEmailId) => {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/admin-email`, {
        userId: owner.id,
        newEmailId,
      })
      return res.data
    },
    onSuccess: async (res) => {
      toast.success(res?.message ?? 'Email ID updated.')
      setEditing(false)
      setError('')
      // Refresh the company/owner data so the row (and the Companies list) show
      // the new address.
      await qc.invalidateQueries({ queryKey: ['platform-tenant', tenantId] })
      qc.invalidateQueries({ queryKey: ['platform-tenants'] })
    },
    onError: (err) => setError(extractApiError(err).message),
  })

  function save() {
    const next = String(value).trim().toLowerCase()
    const parsed = emailSchema().safeParse(next)
    if (!parsed.success) {
      setError(parsed.error.issues?.[0]?.message ?? 'Enter a valid Email ID.')
      return
    }
    if (next === String(owner.emailId ?? '').toLowerCase()) {
      setEditing(false)
      return
    }
    mutation.mutate(next)
  }

  return (
    <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-3 px-4 py-2.5">
      <span className="text-sm text-[color:var(--color-ink-muted)] self-center">
        Email ID
      </span>
      {!editing ? (
        <span className="text-sm text-[color:var(--color-ink-strong)] min-w-0 flex items-center gap-2">
          <span className="min-w-0 break-words">{owner.emailId || '—'}</span>
          <button
            type="button"
            onClick={() => {
              setValue(owner.emailId ?? '')
              setError('')
              setEditing(true)
            }}
            className="inline-flex items-center gap-1 shrink-0 text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
          >
            <Pencil size={12} /> Edit
          </button>
        </span>
      ) : (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <input
              type="email"
              autoFocus
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save()
                if (e.key === 'Escape') {
                  setEditing(false)
                  setError('')
                }
              }}
              disabled={mutation.isPending}
              placeholder="Enter the Email ID"
              className="min-w-0 flex-1 h-9 px-2.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={save}
              disabled={mutation.isPending}
              title="Save"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-50"
            >
              <Check size={15} />
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setError('')
              }}
              disabled={mutation.isPending}
              title="Cancel"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
            >
              <X size={15} />
            </button>
          </div>
          {error ? (
            <p className="mt-1.5 text-xs text-[color:var(--color-priority-urgent)] leading-snug">
              {error}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-[color:var(--color-ink-muted)] leading-snug">
              The member will be signed out of all sessions and notified at their
              previous address.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Full-fat KPI card — matches the `KpiCard` on the Companies list
 *  (TenantsPage) and the Plans page beat-for-beat: rounded-xl chrome,
 *  blurred halo bleed in the top-right, 12×12 tinted icon chip on the
 *  right, 3xl font-display value, uppercase tracking-wider label,
 *  optional muted hint. Same component shape across the platform-
 *  admin surface so the eight tiles here read as the same family as
 *  the four tiles on the Companies list — no UI dialect drift between
 *  the master-admin "list" view and the "drill into one tenant" view. */
function Kpi({ icon: Icon, tone = 'violet', label, value, hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {value ?? 0}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

/** Per-tone tile + halo pair — lifted from `TenantsPage.jsx`'s
 *  KPI_TONES so the colour vocabulary is identical. If you tweak the
 *  Companies-list palette, mirror the change here so the master-admin
 *  KPI strip and the per-company KPI strip stay aligned. */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
}

// ─── shared pill / chip components ────────────────────────────────────

const STATUS_LABEL = {
  active: 'Active',
  suspended: 'Suspended',
  offboarded: 'Offboarded',
  invited: 'Invited',
}

const ORIGIN_LABEL = {
  self_signup: 'Self signup',
  super_admin: 'Admin created',
  demo_seed: 'Demo seed',
}

function PlanPill({ plan }) {
  if (!plan) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  return (
    <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
      {plan}
    </span>
  )
}

function TenantStatusPill({ status }) {
  if (!status) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  const label = STATUS_LABEL[status] ?? status
  const tone =
    status === 'active'
      ? 'text-[color:var(--color-success)]'
      : status === 'invited'
        ? 'text-[color:var(--color-warning)]'
        : status === 'offboarded'
          ? 'text-[color:var(--color-ink-muted)]'
          : 'text-[color:var(--color-priority-urgent)]'
  const dot =
    status === 'active'
      ? 'bg-[color:var(--color-success)]'
      : status === 'invited'
        ? 'bg-[color:var(--color-warning)]'
        : status === 'offboarded'
          ? 'bg-[color:var(--color-ink-muted)]'
          : 'bg-[color:var(--color-priority-urgent)]'
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${tone}`}>
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span className="capitalize">{label}</span>
    </span>
  )
}

const COUNTRY_META_BY_CURRENCY = {
  INR: { code: 'IN', label: 'India' },
  AED: { code: 'AE', label: 'UAE' },
  USD: { code: 'GLOBAL', label: 'Global' },
}

function CountryChip({ country, billingCurrency }) {
  if (country && typeof country === 'string') {
    const iso = country.toUpperCase()
    const label = countryLabels[iso] ?? iso
    const FlagSvg = Flags[iso]
    return (
      <span className="inline-flex items-center gap-2 text-xs text-[color:var(--color-ink)]" title={label}>
        {FlagSvg ? (
          <FlagSvg
            title={label}
            style={{ width: 18, height: 12, flexShrink: 0 }}
          />
        ) : (
          <span aria-hidden="true" className="text-[10px]">🌐</span>
        )}
        <span>{label}</span>
      </span>
    )
  }
  const meta = COUNTRY_META_BY_CURRENCY[billingCurrency]
  if (!meta) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  const FlagSvg = meta.code === 'GLOBAL' ? null : Flags[meta.code]
  return (
    <span className="inline-flex items-center gap-2 text-xs text-[color:var(--color-ink)]" title={meta.label}>
      {FlagSvg ? (
        <FlagSvg
          title={meta.label}
          style={{ width: 18, height: 12, flexShrink: 0 }}
        />
      ) : (
        <span aria-hidden="true" className="text-[10px]">🌐</span>
      )}
      <span>{meta.label}</span>
    </span>
  )
}

// ─── helpers ──────────────────────────────────────────────────────────

function formatBytes(bytes) {
  const v = Number(bytes ?? 0)
  if (v === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(1024)))
  const scaled = v / Math.pow(1024, i)
  const digits = scaled < 10 && i > 0 ? 1 : 0
  return `${formatNumber(scaled, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false })} ${units[i]}`
}

function formatContact(raw) {
  if (!raw) return '—'
  const formatted = formatPhoneNumberIntl(raw)
  return formatted || raw
}

